import { useEffect, useRef, useState } from "react";
import { readUserCache, writeUserCache } from "../lib/dashboardCache";

export type CatalogPrice = {
  monthlyCents: number;
  oneTimeCents: number;
  currency: string;
  stripePriceId: string | null;
  isPremium: boolean;
};

export type CatalogIntegration = {
  slug: string;
  title: string;
  description: string;
  longDescription: string | null;
  category: string;
  iconBrandHex: string | null;
  logoUrl: string | null;
  ourLogoUrl: string | null;
  featured: boolean;
  authType: string | null;
  // Drives the "Ready to go" / "Request access" badge on the catalog card.
  configured: boolean;
  // V49: true only for an admin viewing an admin-only integration (the server
  // withholds it from everyone else) — shows a purple "Admins only" badge in
  // place of "Ready to go".
  adminOnly?: boolean;
  toolCount: number | null;
  price: CatalogPrice;
  included: boolean;
  lifecycleStatus: string;
};

export type CatalogPage = {
  items: ReadonlyArray<CatalogIntegration>;
  total: number;
  page: number;
  pageSize: number;
};

export type CatalogStatus = "loading" | "ready" | "error";

export type UseIntegrationsCatalogResult = {
  status: CatalogStatus;
  page: CatalogPage | null;
  error: string | null;
  // True while a fetch is in flight. The previous page stays rendered
  // (status remains "ready") so paging/searching doesn't blank the grid;
  // callers use this to dim the stale results instead.
  isFetching: boolean;
};

export type CatalogQuery = {
  q?: string | undefined;
  category?: string | undefined;
  premium?: boolean | undefined;
  page?: number | undefined;
  pageSize?: number | undefined;
};

const buildUrl = (query: CatalogQuery): string => {
  const params: URLSearchParams = new URLSearchParams();
  if (query.q && query.q.trim() !== "") params.set("q", query.q.trim());
  if (query.category && query.category !== "All") params.set("category", query.category);
  if (query.premium !== undefined) params.set("premium", String(query.premium));
  params.set("page", String(query.page ?? 1));
  params.set("pageSize", String(query.pageSize ?? 24));
  return `/api/public/integrations?${params.toString()}`;
};

// Only the unfiltered first page is persisted — it's what every catalog
// visit lands on, so a warm open renders instantly. Filtered/paged queries
// stay network-only but keep the previous results on screen while loading.
// The response is viewer-dependent (admins see admin-only entries), hence
// the user-scoped cache.
const DEFAULT_PAGE_RESOURCE = "catalog:default-page";
const CATEGORIES_RESOURCE = "catalog:categories";
// Also viewer-dependent (members are narrowed to what the caller can connect),
// so it rides the same user-scoped cache.
const GROUPS_RESOURCE = "catalog:app-groups";

const isDefaultQuery = (query: CatalogQuery): boolean =>
  (!query.q || query.q.trim() === "") &&
  (!query.category || query.category === "All") &&
  query.premium === undefined &&
  (query.page ?? 1) === 1;

export const useIntegrationsCatalog = (
  query: CatalogQuery,
): UseIntegrationsCatalogResult => {
  const [cached] = useState<CatalogPage | null>(() =>
    isDefaultQuery(query) ? readUserCache<CatalogPage>(DEFAULT_PAGE_RESOURCE) : null,
  );
  const [status, setStatus] = useState<CatalogStatus>(cached !== null ? "ready" : "loading");
  const [page, setPage] = useState<CatalogPage | null>(cached);
  const [error, setError] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState<boolean>(true);
  const hasPage = useRef<boolean>(cached !== null);

  const url: string = buildUrl(query);
  const defaultQuery: boolean = isDefaultQuery(query);

  useEffect(() => {
    let cancelled: boolean = false;
    setIsFetching(true);
    // Keep the previous page rendered while the new query loads; only fall
    // back to the loading state when there's nothing to show yet.
    setStatus(hasPage.current ? "ready" : "loading");
    setError(null);

    void fetch(url, { credentials: "include" })
      .then(async (res: Response): Promise<void> => {
        if (!res.ok) {
          throw new Error(`integrations catalog fetch failed (${res.status})`);
        }
        const body: CatalogPage = (await res.json()) as CatalogPage;
        if (cancelled) return;
        hasPage.current = true;
        setPage(body);
        setStatus("ready");
        setIsFetching(false);
        if (defaultQuery) writeUserCache(DEFAULT_PAGE_RESOURCE, body);
      })
      .catch((err: unknown): void => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setStatus("error");
        setIsFetching(false);
      });

    return (): void => {
      cancelled = true;
    };
  }, [url, defaultQuery]);

  return { status, page, error, isFetching };
};

export type CatalogCategory = {
  category: string;
  count: number;
};

// Distinct catalog categories with counts, fetched once. Backs the filter
// dropdown so it lists every category — not just the ones on the current
// page. Non-fatal on failure: returns an empty list and the caller falls
// back to whatever categories the current page happens to contain.
export const useCatalogCategories = (): ReadonlyArray<CatalogCategory> => {
  const [categories, setCategories] = useState<ReadonlyArray<CatalogCategory>>(
    () => readUserCache<CatalogCategory[]>(CATEGORIES_RESOURCE) ?? [],
  );

  useEffect(() => {
    let cancelled: boolean = false;

    void fetch("/api/public/integrations/categories", {
      credentials: "include",
    })
      .then((res: Response): Promise<CatalogCategory[]> =>
        res.ok ? (res.json() as Promise<CatalogCategory[]>) : Promise.resolve([]),
      )
      .then((body: CatalogCategory[]): void => {
        if (!cancelled && Array.isArray(body) && body.length > 0) {
          setCategories(body);
          writeUserCache(CATEGORIES_RESOURCE, body);
        }
      })
      .catch((): void => {
        /* non-fatal — dropdown falls back to page-derived categories */
      });

    return (): void => {
      cancelled = true;
    };
  }, []);

  return categories;
};

export type IntegrationsBySlug = {
  bySlug: ReadonlyMap<string, CatalogIntegration>;
  // "ready" means the lookup came back and the map is the catalog's answer —
  // a slug missing from it is genuinely not servable. Callers that hide
  // unresolved slugs MUST wait for this, or a slow network / failed request
  // would make them hide everything.
  status: "loading" | "ready" | "error";
};

// Resolves a known set of slugs to their catalog entries in ONE request
// (the public search endpoint takes repeated ?slug= params). Backs every
// surface that renders integrations chosen elsewhere — the pinned apps on
// an invite, the "connect these next" cards in onboarding — where we hold
// slugs but need titles and logos. Unknown or retired slugs are simply
// absent from the map, so callers render what the catalog can still serve.
export const useIntegrationsBySlug = (slugs: ReadonlyArray<string>): IntegrationsBySlug => {
  const [bySlug, setBySlug] = useState<ReadonlyMap<string, CatalogIntegration>>(new Map());
  const [status, setStatus] = useState<IntegrationsBySlug["status"]>("loading");
  // Sorted + joined so a re-render with the same set (new array identity)
  // doesn't refetch.
  const key: string = [...new Set(slugs)].sort().join(",");

  useEffect(() => {
    if (key === "") {
      setBySlug(new Map());
      setStatus("ready");
      return;
    }
    let cancelled: boolean = false;
    setStatus("loading");
    const wanted: string[] = key.split(",");
    const params = new URLSearchParams();
    for (const slug of wanted) params.append("slug", slug);
    params.set("page", "1");
    params.set("pageSize", String(wanted.length));

    void fetch(`/api/public/integrations?${params.toString()}`, { credentials: "include" })
      .then((res: Response): Promise<CatalogPage> =>
        res.ok ? (res.json() as Promise<CatalogPage>) : Promise.reject(new Error(String(res.status))),
      )
      .then((body: CatalogPage): void => {
        if (cancelled) return;
        setBySlug(new Map(body.items.map((item) => [item.slug, item])));
        setStatus("ready");
      })
      .catch((): void => {
        // Non-fatal, but callers must be able to tell this apart from an
        // honest empty answer — hence the explicit error state rather than
        // leaving them with a silently empty map.
        if (!cancelled) setStatus("error");
      });

    return (): void => {
      cancelled = true;
    };
  }, [key]);

  return { bySlug, status };
};

// A pinnable "any of these" option (IP-588) — e.g. "Email", whose members are
// the mailbox providers a customer might use. Members arrive already narrowed
// to what the caller can connect, so an empty `members` means we currently
// have nothing to offer for that job.
export type AppChoiceGroup = {
  id: string;
  title: string;
  prompt: string;
  members: ReadonlyArray<CatalogIntegration>;
};

export const APP_GROUP_PREFIX = "group:";

export const isAppGroupToken = (token: string): boolean => token.startsWith(APP_GROUP_PREFIX);

export type AppChoiceGroupsResult = {
  groups: ReadonlyArray<AppChoiceGroup>;
  // "loading" means the empty list is not an answer yet. Callers that decide
  // something from the ABSENCE of a group (the onboarding-call gate) MUST wait
  // for this; callers that only render what's there can ignore it. Settles on
  // "ready" even when the request failed — see the catch.
  status: "loading" | "ready";
};

// Fetched once per surface that needs them (the admin picker, the customer's
// onboarding + locked screens). Served from ztc-core so the member list has a
// single definition rather than one copy per caller.
export const useAppChoiceGroups = (): AppChoiceGroupsResult => {
  // Seeded from the user-scoped cache like the sibling category hook, so a
  // warm visit resolves on the first paint instead of holding its callers
  // through a round trip.
  const [cached] = useState<AppChoiceGroup[] | null>(() =>
    readUserCache<AppChoiceGroup[]>(GROUPS_RESOURCE),
  );
  const [groups, setGroups] = useState<ReadonlyArray<AppChoiceGroup>>(cached ?? []);
  const [status, setStatus] = useState<AppChoiceGroupsResult["status"]>(
    cached !== null ? "ready" : "loading",
  );

  useEffect(() => {
    let cancelled: boolean = false;
    void fetch("/api/public/integrations/groups", { credentials: "include" })
      .then((res: Response): Promise<AppChoiceGroup[]> =>
        res.ok ? (res.json() as Promise<AppChoiceGroup[]>) : Promise.resolve([]),
      )
      .then((body: AppChoiceGroup[]): void => {
        if (cancelled || !Array.isArray(body)) return;
        setGroups(body);
        setStatus("ready");
        if (body.length > 0) writeUserCache(GROUPS_RESOURCE, body);
      })
      .catch((): void => {
        // Non-fatal — surfaces fall back to showing no group options. Still
        // "ready": a caller held open forever by a failed request is worse
        // than one that proceeds without groups.
        if (!cancelled) setStatus("ready");
      });
    return (): void => {
      cancelled = true;
    };
  }, []);

  return { groups, status };
};
